import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { WHATSAPP_URL } from "../lib/constants";
import { usePageMeta } from "../lib/usePageMeta";
import { api } from "../lib/api";
import {
  Users, Bus, Ship, Building2, HeartHandshake, ShieldCheck, MapPin, Sparkles,
  Sun, Compass, Coffee, Calendar, ArrowRight, HelpCircle, Star, Church,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1594736797933-d0501ba2fe65?auto=format&fit=crop&w=1600&q=80";
const CRUISE_IMG =
  "https://images.unsplash.com/photo-1548574505-5e239809ee19?auto=format&fit=crop&w=1200&q=80";
const HOTEL_IMG =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=80";
const GROUP_IMG =
  "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1200&q=80";

const PILLARS = [
  {
    icon: Users,
    title: "Viagens Acompanhadas em Grupo",
    body:
      "Guia dedicado do início ao fim, transfer porta-a-porta, roteiros com ritmo pensado para quem viaja sem pressa e a segurança de estar sempre em boa companhia.",
  },
  {
    icon: Compass,
    title: "Roteiros Tranquilos, Sem Cansaço",
    body:
      "Pouca troca de hotel, distâncias curtas entre atrações, pausas confortáveis para café e almoço. Aproveitar sem ficar exausto é o que buscamos em cada dia.",
  },
  {
    icon: Bus,
    title: "Ônibus-Leito Premium",
    body:
      "Frota parceira com poltronas reclináveis 180°, Wi-Fi Starlink a bordo, banheiro e tomadas individuais. Ideal para quem prefere menos aeroportos e mais conforto.",
  },
  {
    icon: Ship,
    title: "Cruzeiros Guiados",
    body:
      "Cruzeiros marítimos e fluviais com o hotel viajando junto: você desfaz a mala uma vez e conhece várias cidades. Excelente logística e atendimento médico a bordo.",
  },
  {
    icon: Building2,
    title: "Hotéis Confortáveis e Acessíveis",
    body:
      "Selecionamos hospedagens em bairros centrais, planos e seguros. Quartos amplos, elevador, café-da-manhã farto e recepção que fala português.",
  },
  {
    icon: HeartHandshake,
    title: "Atendimento Personalizado",
    body:
      "Contato direto no WhatsApp com um consultor Orion — antes, durante e depois da viagem. Dúvidas de medicamentos, seguro-saúde e logística resolvidas em minutos.",
  },
];

const HIGHLIGHTS = [
  {
    icon: Church,
    title: "Roteiros Religiosos",
    body:
      "Aparecida do Norte, Fátima em Portugal, Lourdes na França, Santiago de Compostela, Terra Santa. Peregrinações com missas e visitas guiadas.",
  },
  {
    icon: Sun,
    title: "Termas e Águas Quentes",
    body:
      "Poços de Caldas, Rio Quente, Caldas Novas. Roteiros com balneários hidrominerais, hotéis com estrutura completa e ambiente sereno.",
  },
  {
    icon: Star,
    title: "Cidades Históricas",
    body:
      "Ouro Preto, Petrópolis, Gramado, Trancoso. Passeios a pé em ritmo tranquilo, café colonial, artesanato e muita história.",
  },
  {
    icon: Ship,
    title: "Cruzeiros pela Costa",
    body:
      "Costa brasileira, Caribe e Mediterrâneo. Cabines confortáveis, entretenimento a bordo, gastronomia farta e sem preocupação com bagagem.",
  },
];

const TRUST = [
  { icon: ShieldCheck, title: "Guia acompanhante", body: "Do embarque ao retorno." },
  { icon: HeartHandshake, title: "Seguro-viagem", body: "Cobertura ampliada para 60+." },
  { icon: MapPin, title: "Bairros seguros", body: "Hotéis centrais, com fácil acesso." },
  { icon: Coffee, title: "Ritmo humano", body: "Pausas planejadas, sem correria." },
];

const FAQ = [
  {
    q: "Preciso viajar acompanhado ou posso ir sozinho?",
    a: "Você pode ir sozinho e se juntar ao nosso grupo — muitos clientes fazem novas amizades durante a viagem. Se preferir, também organizamos roteiros exclusivos para casais, famílias multigeracionais ou grupos fechados de amigos.",
  },
  {
    q: "Como funciona a questão da saúde e medicamentos durante a viagem?",
    a: "Fazemos um briefing pré-viagem para orientar sobre bagagem de medicamentos, receitas médicas, seguro-viagem com cobertura ampliada e contatos de assistência 24h no destino. Nos cruzeiros, há atendimento médico a bordo e nos roteiros terrestres o guia mantém contato com clínicas e hospitais próximos.",
  },
  {
    q: "Vocês oferecem viagens só de ônibus, sem avião?",
    a: "Sim. Para quem prefere não voar, temos roteiros terrestres com ônibus-leito premium (poltronas reclináveis 180°, Wi-Fi Starlink, banheiro a bordo). Ideal para destinos no Sudeste, Sul e Nordeste do Brasil.",
  },
  {
    q: "As caminhadas são muito longas nos passeios?",
    a: "Não. Os roteiros para terceira idade são pensados com distâncias curtas entre atrações, pausas planejadas e transporte de apoio sempre próximo. Sempre respeitamos o ritmo do grupo — não é excursão apressada.",
  },
  {
    q: "Qual a diferença entre viagem em grupo e viagem individual?",
    a: "No grupo você tem guia dedicado, valores mais amigáveis, roteiro fechado com pontos mais visitados e a companhia de outros viajantes. No individual, montamos o roteiro sob medida com os destinos, hotéis e ritmo que você preferir — com o mesmo suporte 24h da Orion.",
  },
  {
    q: "É seguro viajar internacionalmente na terceira idade?",
    a: "Sim, desde que com boa preparação. Cuidamos do seguro-viagem com cobertura ampliada para 60+, orientamos sobre vacinas necessárias, escolhemos hotéis em bairros seguros e centrais e mantemos o contato com você durante toda a viagem. Destinos favoritos: Portugal, Espanha, Itália, França e Terra Santa.",
  },
];

const HOW = [
  { step: "01", title: "Conversa inicial", body: "Você fala com um consultor Orion no WhatsApp e conta o que sonha — o destino, o ritmo, quem vai junto." },
  { step: "02", title: "Roteiro sugerido", body: "Recebe uma proposta com opções de datas, hotéis, transporte, passeios e valores fechados. Sem letra miúda." },
  { step: "03", title: "Reserva com segurança", body: "Contrato formal, parcelamento facilitado e cobertura de seguro-viagem já incluída no pacote." },
  { step: "04", title: "Suporte durante e depois", body: "Nosso time acompanha você no dia-a-dia da viagem — e continua disponível quando você voltar." },
];

export default function TerceiraIdade() {
  usePageMeta(
    "Viagens para Terceira Idade · Grupos, Cruzeiros e Ônibus-Leito · Viagens Orion",
    "Viagens especiais para terceira idade: grupos acompanhados por guia, cruzeiros marítimos, ônibus-leito premium, hotéis confortáveis em bairros seguros e ritmo tranquilo. Atendimento personalizado no WhatsApp."
  );

  const [openFaq, setOpenFaq] = React.useState(0);
  const [relatedPosts, setRelatedPosts] = React.useState([]);

  // Fetch content related to Terceira Idade (curated by focus keywords)
  React.useEffect(() => {
    let cancelled = false;
    const KEYWORDS = ["terceira idade", "60+", "aparecida", "fátima", "santuário", "cruzeiro", "ônibus-leito", "poços de caldas", "termas", "aposentado"];
    Promise.all(
      KEYWORDS.map((q) =>
        api.get("/posts", { params: { q, limit: 3 } }).then((r) => r.data?.items || []).catch(() => [])
      )
    ).then((batches) => {
      if (cancelled) return;
      const seen = new Set();
      const merged = [];
      for (const arr of batches) {
        for (const p of arr) {
          if (!p?.slug || seen.has(p.slug)) continue;
          seen.add(p.slug);
          merged.push(p);
          if (merged.length >= 6) break;
        }
        if (merged.length >= 6) break;
      }
      setRelatedPosts(merged);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1" data-testid="terceira-idade-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#2C1139] via-[#3d1b52] to-[#0d0413] text-white">
          <span
            className="absolute inset-0 opacity-30"
            aria-hidden
            style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
          />
          <span className="absolute inset-0 bg-gradient-to-r from-[#2C1139]/95 via-[#2C1139]/70 to-transparent" aria-hidden />
          <span className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-orion-orange/25 blur-3xl" aria-hidden />

          <div className="relative container-orion py-16 md:py-28 max-w-6xl">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-5">
              <HeartHandshake size={13} /> Viagens Orion · Terceira Idade
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] max-w-4xl">
              <span className="text-orion-orange">Viagens tranquilas</span> para você aproveitar a vida com conforto e segurança.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
              Somos a agência de viagens especializada em <strong>roteiros para 60+</strong>: grupos acompanhados por guia dedicado, cruzeiros marítimos, ônibus-leito premium com Wi-Fi Starlink, hotéis confortáveis em bairros seguros e um consultor Orion no seu WhatsApp — antes, durante e depois da viagem.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#1EBE57] transition-all shadow-[0_14px_38px_rgba(37,211,102,0.45)] hover:-translate-y-0.5"
                data-testid="ti-cta-wa"
              >
                <WhatsAppIcon size={16} color="#ffffff" /> Falar com um Consultor
              </a>
              <a href="#servicos" className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:border-orion-orange hover:text-orion-orange transition-colors">
                Ver Roteiros <ArrowRight size={14} />
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4 md:gap-8 text-[10px] uppercase tracking-[0.3em] text-white/75">
              <span className="inline-flex items-center gap-2"><Users size={12} className="text-orion-gold" /> Grupos acompanhados</span>
              <span className="inline-flex items-center gap-2"><Ship size={12} className="text-orion-gold" /> Cruzeiros</span>
              <span className="inline-flex items-center gap-2"><Bus size={12} className="text-orion-gold" /> Ônibus-leito premium</span>
              <span className="inline-flex items-center gap-2"><Building2 size={12} className="text-orion-gold" /> Hotéis confortáveis</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck size={12} className="text-orion-gold" /> Seguro incluído</span>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="border-y border-[#EFEAE0] bg-[#FAF7F0]">
          <div className="container-orion py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {TRUST.map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`ti-trust-${i}`}>
                <t.icon size={22} className="text-orion-orange" />
                <div className="font-serif text-orion-purple text-sm md:text-base">{t.title}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#594A63] leading-snug">{t.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PILLARS / SERVICES */}
        <section id="servicos" className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">O que oferecemos</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Um ecossistema completo pensado para viajar bem depois dos 60.
            </h2>
            <p className="mt-4 text-[#594A63] text-base md:text-lg leading-relaxed">
              A Orion é a extensão da sua família em viagem. Cada detalhe é pensado no ritmo, na segurança e no conforto de quem quer aproveitar sem correria — do transfer da porta de casa ao check-in de volta.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="group border border-[#EFEAE0] p-7 hover:border-orion-orange hover:shadow-[0_18px_40px_rgba(44,17,57,0.12)] transition-all bg-white"
                data-testid={`ti-pillar-${i}`}
              >
                <p.icon size={30} className="text-orion-orange mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.6} />
                <h3 className="font-serif text-xl text-orion-purple mb-3 leading-snug">{p.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHT — CRUISE & BUS */}
        <section className="bg-[#2C1139] text-white overflow-hidden">
          <div className="container-orion py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Destaque</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05]">
                Cruzeiros e ônibus-leito — menos aeroportos, mais conforto.
              </h2>
              <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
                Quem passa dos 60 sabe: aeroporto cansa. Por isso oferecemos roteiros <strong>quase sem voos</strong>: cruzeiros marítimos que levam você a várias cidades sem trocar a mala e ônibus-leito premium com poltronas reclináveis 180°, Wi-Fi Starlink, banheiro e tomadas individuais.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/85">
                <li className="flex items-start gap-2"><Ship size={14} className="text-orion-gold mt-1" /> Cabines confortáveis e atendimento médico a bordo</li>
                <li className="flex items-start gap-2"><Bus size={14} className="text-orion-gold mt-1" /> Poltronas reclináveis 180° com Wi-Fi Starlink</li>
                <li className="flex items-start gap-2"><Coffee size={14} className="text-orion-gold mt-1" /> Pausas planejadas a cada 2h de viagem terrestre</li>
                <li className="flex items-start gap-2"><Calendar size={14} className="text-orion-gold mt-1" /> Cronograma sem correria — respeitando o ritmo do grupo</li>
              </ul>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center gap-2 bg-orion-orange px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#E04828] transition-all"
                data-testid="ti-cruise-cta"
              >
                <WhatsAppIcon size={14} color="#ffffff" /> Falar sobre cruzeiros e ônibus
              </a>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={CRUISE_IMG} alt="Cruzeiro Orion" className="aspect-[4/5] object-cover" />
              <img src={GROUP_IMG} alt="Grupo em viagem" className="aspect-[4/5] object-cover mt-8" />
              <img src={HOTEL_IMG} alt="Hotel confortável" className="aspect-[4/5] object-cover col-span-2 h-64" />
            </div>
          </div>
        </section>

        {/* HIGHLIGHTS BLOCKS */}
        <section className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Roteiros favoritos</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Destinos que a nossa terceira idade ama.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="border border-[#EFEAE0] p-6 bg-white hover:border-orion-orange transition-colors" data-testid={`ti-hi-${i}`}>
                <h.icon size={26} className="text-orion-orange mb-3" strokeWidth={1.6} />
                <h3 className="font-serif text-lg text-orion-purple mb-2 leading-snug">{h.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="bg-[#FAF7F0] border-y border-[#EFEAE0]">
          <div className="container-orion py-20 md:py-28">
            <div className="max-w-2xl mb-14">
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Como funciona</div>
              <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
                Do primeiro "Oi!" no WhatsApp até o "Cheguei bem em casa."
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {HOW.map((h, i) => (
                <div key={i} className="bg-white p-6 border border-[#EFEAE0]" data-testid={`ti-how-${i}`}>
                  <div className="font-serif text-3xl text-orion-orange mb-2">{h.step}</div>
                  <h3 className="font-serif text-lg text-orion-purple mb-2">{h.title}</h3>
                  <p className="text-sm text-[#594A63] leading-relaxed">{h.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container-orion py-20 md:py-28">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <HelpCircle size={26} className="mx-auto text-orion-orange mb-3" />
              <h2 className="font-serif text-3xl md:text-5xl text-orion-purple">Perguntas frequentes</h2>
            </div>
            <div className="divide-y divide-[#EFEAE0] border-y border-[#EFEAE0]">
              {FAQ.map((item, i) => (
                <div key={i}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-start justify-between text-left p-5 md:p-6 gap-6 group"
                    data-testid={`ti-faq-q-${i}`}
                  >
                    <span className="font-serif text-base md:text-lg text-orion-purple leading-snug group-hover:text-orion-orange transition-colors">
                      {item.q}
                    </span>
                    <span className={`shrink-0 mt-1 text-orion-orange transition-transform ${openFaq === i ? "rotate-45" : ""}`}>
                      <ArrowRight size={20} strokeWidth={2} className="rotate-90" />
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 md:px-6 pb-6 text-[15px] text-[#594A63] leading-relaxed" data-testid={`ti-faq-a-${i}`}>
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RELATED CONTENT — Terceira Idade */}
        {relatedPosts.length > 0 && (
          <section className="bg-white border-t border-[#EFEAE0]">
            <div className="container-orion py-20 md:py-24">
              <div className="max-w-2xl mb-10">
                <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Leituras selecionadas</div>
                <h2 className="font-serif text-3xl md:text-4xl text-orion-purple leading-tight">
                  Conteúdos que preparam a viagem certa para você.
                </h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/artigo/${p.slug}`}
                    className="block group border border-[#EFEAE0] hover:border-orion-orange transition-colors overflow-hidden bg-white"
                    data-testid={`ti-related-${p.slug}`}
                  >
                    {p.cover_image && (
                      <div className="aspect-[4/3] overflow-hidden bg-[#F4F1EC]">
                        <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-orion-orange mb-2">
                        {(p.categories && p.categories[0]) || "Roteiro"}
                      </div>
                      <h3 className="font-serif text-lg text-orion-purple leading-snug group-hover:text-orion-orange transition-colors line-clamp-2">
                        {p.title}
                      </h3>
                      {p.excerpt && (
                        <p className="mt-2 text-sm text-[#594A63] leading-relaxed line-clamp-3">{p.excerpt}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="relative bg-gradient-to-br from-orion-orange via-[#E04828] to-[#B32E10] text-white overflow-hidden">
          <span className="absolute inset-0 opacity-20" aria-hidden style={{ backgroundImage: `url(${GROUP_IMG})`, backgroundSize: "cover" }} />
          <span className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden />
          <div className="relative container-orion py-20 md:py-28 text-center max-w-3xl">
            <Sparkles size={30} className="mx-auto text-white mb-5" />
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">
              Vamos planejar a sua próxima aventura?
            </h2>
            <p className="mt-5 text-white/90 text-base md:text-lg">
              Envie uma mensagem no WhatsApp e um dos nossos consultores retorna com opções sob medida — no seu ritmo, no seu conforto.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-white text-orion-purple px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-orion-cream transition-all hover:-translate-y-0.5 shadow-[0_14px_38px_rgba(0,0,0,0.25)]"
                data-testid="ti-cta-bottom"
              >
                <WhatsAppIcon size={16} color="#2C1139" /> Falar no WhatsApp
              </a>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white/10 transition-colors"
              >
                Conhecer a Orion
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
